<template>
  <div>
    <div
      class="amount-input-group"
      :class="[`amount-input-group--${size}`, { disabled, 'has-error': !!error }]"
    >
      <span v-if="!isPostfix" class="amount-prefix">{{ resolvedSymbol }}</span>
      <base-input class="amount-input-wrapper mb-0">
        <el-input-number
          :controls="controls"
          :value="value == null ? undefined : value"
          :min="min"
          :max="max"
          :precision="precision"
          :step="step"
          :placeholder="placeholder"
          :disabled="disabled"
          @input="onInput"
          @change="onChange"
        />
      </base-input>
      <span v-if="isPostfix" class="amount-prefix">{{ resolvedSymbol }}</span>
    </div>
    <small v-if="errorMessage" class="money-input__error">{{ errorMessage }}</small>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { getCurrencyInfo, getGroupSettings } from '@/util/currencyFormat'

// `numberFormat` is intentionally omitted: el-input-number does not honor
// EU-style comma decimal separators without a formatter/parser pair, and the
// existing 9 amount-input call sites don't honor it either. Display sites
// still pass numberFormat to formatCurrency() — that's the right place.
//
// Runtime prop declarations (not TS-only) so nullable props don't emit
// `type: [T, null]` — Vue 2.7's SFC compiler turns `T | null` into a
// literal `null` in the type array, which Vue rejects as a non-constructor.
const props = defineProps({
  value: { type: Number as PropType<number | null>, default: null },
  min: { type: Number, default: 0 },
  max: { type: Number, default: Number.MAX_SAFE_INTEGER },
  precision: { type: Number, default: 2 },
  step: { type: Number, default: 1 },
  placeholder: { type: String, default: '0.00' },
  disabled: { type: Boolean, default: false },
  controls: { type: Boolean, default: false },
  error: { type: [Boolean, String] as PropType<boolean | string>, default: false },
  currency: { type: String as PropType<string | null>, default: null },
  currencySymbol: { type: String as PropType<string | null>, default: null },
  currencyFormat: { type: Number as PropType<number | null>, default: null },
  // 'sm' = the 32px row height used inside editable lists (price tiers)
  size: { type: String as PropType<'sm' | 'md'>, default: 'md' }
})

const emit = defineEmits<{
  (e: 'input', value: number | null): void
  (e: 'change', value: number | null): void
}>()

const groupSettings = computed(() => getGroupSettings())

const resolvedCurrencyCode = computed(
  () => props.currency || groupSettings.value.currency || 'USD'
)

const currencyInfo = computed(() => getCurrencyInfo(resolvedCurrencyCode.value))

const resolvedSymbol = computed(() => props.currencySymbol || currencyInfo.value.symbol)

const isPostfix = computed(() => currencyInfo.value.postfix)

const errorMessage = computed(() => (typeof props.error === 'string' ? props.error : ''))

// el-input-number emits `undefined` (not null) when the field is cleared.
// Coerce so consumers see a clean `null` and the typed signature holds.
function onInput(val: number | null | undefined) {
  emit('input', val == null ? null : val)
}

function onChange(val: number | null | undefined) {
  emit('change', val == null ? null : val)
}
</script>

<script lang="ts">
export default { name: 'MoneyInput' }
</script>

<style lang="scss" scoped>
.amount-input-group {
  display: flex;
  align-items: stretch;
  border: 1px solid rgba(225, 78, 202, 0.4);
  border-radius: 0.4285rem;
  overflow: hidden;
  background: rgba(30, 30, 47, 0.6);

  &:focus-within {
    border-color: #e14eca;
  }

  &.has-error {
    border-color: #ec250d;
  }

  &.disabled {
    opacity: 0.6;
    cursor: not-allowed;

    .amount-prefix {
      background: rgba(225, 78, 202, 0.08);
    }
  }
}

.amount-prefix {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.875rem;
  background: rgba(225, 78, 202, 0.15);
  font-weight: 600;
  font-size: 0.875rem;
  min-width: 40px;
  color: #e14eca;
}

.amount-input-wrapper {
  flex: 1;

  ::v-deep .form-control {
    border: none;
    border-radius: 0;
    background: transparent;
  }

  ::v-deep .el-input-number {
    width: 100%;
  }

  ::v-deep .el-input-number .el-input__inner {
    text-align: left;
    color: white;
    background: transparent;
    border: none;
  }
}

.amount-input-group--sm {
  height: 32px;

  .amount-prefix {
    padding: 0 8px;
    font-size: 12px;
    min-width: 28px;
  }

  .amount-input-wrapper ::v-deep .el-input-number .el-input__inner {
    height: 30px;
    line-height: 30px;
    font-size: 13px;
    padding: 0 8px;
  }

  .amount-input-wrapper ::v-deep .form-group {
    margin-bottom: 0;
  }
}

.money-input__error {
  display: block;
  margin-top: 4px;
  color: #ec250d;
  font-size: 0.75rem;
}
</style>

<style lang="scss">
.white-content .amount-input-group {
  background: #ffffff;
  border-color: rgba(225, 78, 202, 0.3);

  .el-input-number .el-input__inner {
    color: #344675;
  }
}
</style>
